import { makeStyles } from '@material-ui/styles';
import { Autocomplete } from '@material-ui/lab';
import useSessionStore from '@services/stores/SessionStore';
import { useEffect, useState } from 'react';
import { useTranslation } from '@tools/i18n';
import { TextField } from '@material-ui/core';
import { ucfirst } from '@services/Utils';
import theme from '@components/theme';

const useStyles = makeStyles({
    input: {
        '& .MuiOutlinedInput-notchedOutline': {
            padding: ' 6px 10px',
            borderRadius: '0 !important',
            border: '2px solid #041e3a63',
            outline: 0,
        },
        '& .MuiOutlinedInput-root': {
            padding: 6,
        },
        '&.has-errors .MuiOutlinedInput-notchedOutline2': {
            borderColor: theme.palette.primary.darker,
        },
    },
    label: {
        fontSize: 12,
        fontFamily: theme.typography.fontFamily,
    },
    inputWrapper: {
        display: 'flex',
        flexDirection: 'column',
    },
    errors: {
        display: 'grid',
        padding: '6px 8px',
        color: '#ffffff',
        gap: 4,
        fontSize: 12,
        background: theme.palette.primary.darker,
    },
}, { name: 'np-country-selector' });

interface NewportCountrySelectorProps {
    onChange: (countryCode: CountryOption | null) => void;
    label?: null | string;
    errors?: any[];
}

interface CountryOption {
    code: string;
    label: string;
}

export default function NewportCountrySelector(
    {
        onChange,
        label = null,
        errors = [],
    }: NewportCountrySelectorProps,
) {
    const classes = useStyles();
    const sessionMethods = useSessionStore((state) => state.methods);
    const countries = useSessionStore((state) => state.session.countries);
    const { t } = useTranslation();
    const autoCountryCode = useSessionStore((state) => state.session.geoIpCountry);
    const [id, setId] = useState(
        `np-input-${Math.ceil((Date.now() / 1000) * Math.random()) + Math.floor(Math.random() * 100000)}`,
    );
    const [value, setValue] = useState<CountryOption | null>(null);
    const [options, setOptions] = useState<CountryOption[]>([]);

    useEffect(() => {
        sessionMethods.getCountries().then((countries) => {
            const options = countries.map((country) => {
                return {
                    code: country.iso || '',
                    label: country.translated.name || country.name || '',
                };
            }).sort((a, b) => a.label.localeCompare(b.label, ['sv', 'en'], { sensitivity: 'case' }));

            setOptions(options);

            if (options.length === 1) {
                const autoVal = options[0] || null;
                setValue(autoVal);
            } else {
                const auto = options.find(option => option.code === autoCountryCode) || null;
                setValue(auto);
            }
        });
    }, [countries]);

    useEffect(() => {
        if (onChange) {
            onChange(value);
        }
    }, [value]);

    function renderLabel(label: string | null) {
        if (!label) {
            return null;
        }

        return <>
            <label className={classes.label} htmlFor={id}>
                {label}
            </label>
        </>;
    }

    function validation(errors: string[]) {
        if (!Array.isArray(errors) || errors.length <= 0) {
            return null;
        }

        return <div className={classes.errors}>
            {errors.map((error) => {
                return <span>{ucfirst(error)}</span>;
            })}
        </div>;
    }

    function errorClass() {
        return errors?.length > 0 ? 'has-errors' : null;
    }

    return <div className={classes.inputWrapper}>
        {renderLabel(label)}
        {/** @ts-ignore */}
        <Autocomplete
            className={[classes.input, errorClass()].join(' ')}
            // @ts-ignore
            renderInput={(params) => <TextField
                {...params}
                variant={'outlined'}
                id={id}
            />}
            renderOption={(props, option) => {
                return `${props?.label} (${props?.code})`;
            }}
            getOptionLabel={(option) => option.label}
            options={options}
            value={value}
            onChange={(event, value, reason) => {
                setValue(value);
            }}
        />
        {validation(errors)}
    </div>;
}
